import { useState, useEffect } from 'react';
import { FiUsers, FiSettings, FiTrendingUp, FiAlertCircle, FiBarChart, FiEdit2, FiShield } from 'react-icons/fi';
// import { format } from 'date-fns'; // Not used
import { Link } from 'react-router-dom';
// import { userAPI as usersApi } from '../../api/users'; // Not used
import { organizationAPI as organizationsApi } from '../../api/organizations';
// import { auditAPI } from '../../api/audit-logging'; // Not used
// import { usageBillingAPI as usageBillingApi } from '../../api/usage-billing'; // Not used
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/ui/Input/Input';
import Modal from '../../components/Modal/Modal';
// import { Badge } from '../../components/ui/Badge/Badge'; // Not used
import { useOrganization } from '../../contexts/OrganizationContext';
import { usePermissions, Permission } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/useToast';
import styles from './AdminDashboard.module.css';

import { logger } from '../../utils/logger';

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalOrganizations: number;
  securityEvents: number;
  recentAuditLogs: number;
  monthlyUsage: number;
  pendingInvites: number;
  complianceScore: number;
}

// Each module entry declares the backend Permission that gates the
// corresponding route. Tiles are filtered at render time via
// `usePermissions().hasPermission()` so viewers and members do not see
// entry points to pages they cannot use.
const ADMIN_MODULES = [
  {
    id: 'organizations',
    title: 'Organizations',
    description: 'Manage organizations, members, and settings',
    icon: FiUsers,
    color: 'var(--color-primary)',
    path: '/admin/organizations',
    permission: Permission.ORG_READ,
  },
  {
    id: 'users',
    title: 'User Management',
    description: 'Invite, manage, and remove team members',
    icon: FiUsers,
    color: 'var(--color-info)',
    path: '/admin/users',
    permission: Permission.ORG_INVITE,
  },
  {
    id: 'permissions',
    title: 'Roles & Permissions',
    description: 'Review what each role can do in this organization',
    icon: FiShield,
    color: 'var(--color-success)',
    path: '/admin/permissions',
    permission: Permission.ORG_READ,
  },
  {
    id: 'billing',
    title: 'Billing & Usage',
    description: 'Track usage and manage billing',
    icon: FiBarChart,
    color: 'var(--color-secondary)',
    path: '/admin/billing',
    permission: Permission.ORG_READ,
  },
  {
    id: 'audit',
    title: 'Audit Logs',
    description: 'Security events and compliance reporting',
    icon: FiAlertCircle,
    color: 'var(--color-error)',
    path: '/admin/audit',
    permission: Permission.ORG_VIEW_AUDIT_LOG,
  },
];

export default function AdminDashboard() {
  const { currentOrganization, refreshOrganizations, setCurrentOrganization } = useOrganization();
  const { canManageOrganization, hasPermission } = usePermissions();
  const { addToast } = useToast();

  // Organization rename modal state.
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  const openRenameModal = () => {
    setRenameValue(currentOrganization?.name || '');
    setRenameError(null);
    setShowRenameModal(true);
  };

  const handleRename = async () => {
    if (!currentOrganization) return;
    const trimmed = renameValue.trim();
    if (trimmed.length < 2 || trimmed.length > 100) {
      setRenameError('Name must be between 2 and 100 characters.');
      return;
    }
    if (trimmed === currentOrganization.name) {
      setShowRenameModal(false);
      return;
    }
    try {
      setRenaming(true);
      setRenameError(null);
      const updated = await organizationsApi.updateOrganization(currentOrganization.id, { name: trimmed });
      setCurrentOrganization({ ...currentOrganization, ...updated });
      await refreshOrganizations();
      addToast('Organization renamed', 'success');
      setShowRenameModal(false);
    } catch (err: any) {
      setRenameError(err?.message || 'Failed to rename organization');
    } finally {
      setRenaming(false);
    }
  };

  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeUsers: 0,
    totalOrganizations: 0,
    securityEvents: 0,
    recentAuditLogs: 0,
    monthlyUsage: 0,
    pendingInvites: 0,
    complianceScore: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Load dashboard data immediately, don't wait for organization
    loadDashboardData();
  }, [currentOrganization?.id]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get organization ID from context or localStorage
      const primaryOrgId = currentOrganization?.id || localStorage.getItem('current_organization_id');

      if (!primaryOrgId) {
        logger.log('⏭️ No organization ID available yet, will retry when organization loads');
        setLoading(false);
        return;
      }

      logger.log('🏢 Loading admin dashboard for organization:', primaryOrgId);

      // Load dashboard statistics - only call endpoints that exist in backend
      const [
        orgsResponse,
        membersResponse,
        activitiesResponse
      ] = await Promise.allSettled([
        // Organizations endpoint - working ✅
        organizationsApi.getUserOrganizations(),
        // Members endpoint - working ✅
        organizationsApi.getOrganizationMembers(primaryOrgId, { limit: 1 }),
        // Activities endpoint - working ✅
        organizationsApi.getActivities({ limit: 1 })
      ]);

      // Process results - handle API failures gracefully
      const newStats: DashboardStats = {
        totalUsers: membersResponse.status === 'fulfilled' ? membersResponse.value.total || 0 : 0,
        activeUsers: membersResponse.status === 'fulfilled' ? membersResponse.value.total || 0 : 0,
        totalOrganizations: orgsResponse.status === 'fulfilled' ? orgsResponse.value.total || 0 : 0,
        securityEvents: 0, // Not available yet
        recentAuditLogs: activitiesResponse.status === 'fulfilled' ? activitiesResponse.value.total || 0 : 0,
        monthlyUsage: 0, // Not available yet
        pendingInvites: 0, // Not available yet
        complianceScore: 95 // Placeholder
      };

      setStats(newStats);

      logger.log('📊 Admin Dashboard loaded successfully');

    } catch (err) {
      logger.error('Error loading dashboard:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // `hasPermission` from the `usePermissions` hook is used directly — the
  // previous local placeholder against a static array has been removed.

  const getStatCards = () => [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: FiUsers,
      color: 'var(--color-primary)',
      trend: `${stats.activeUsers} active`,
      link: '/admin/users'
    },
    {
      title: 'Organizations',
      value: stats.totalOrganizations,
      icon: FiUsers,
      color: 'var(--color-info)',
      trend: `${stats.pendingInvites} pending invites`,
      link: '/admin/organizations'
    },
    {
      title: 'Security Events',
      value: stats.securityEvents,
      icon: FiAlertCircle,
      color: 'var(--color-warning)',
      trend: 'Last 30 days',
      link: '/admin/audit'
    },
    {
      title: 'Monthly Usage',
      value: `${stats.monthlyUsage}%`,
      icon: FiTrendingUp,
      color: 'var(--color-success)',
      trend: 'of quota used',
      link: '/admin/billing'
    }
  ];

  const renderStatCard = (stat: any) => (
    <Link to={stat.link} key={stat.title} className={styles.statLink}>
      <Card className={styles.statCard}>
        <div className={styles.statIcon} style={{ color: stat.color }}>
          <stat.icon />
        </div>
        <div className={styles.statContent}>
          <h3 className={styles.statValue}>{stat.value}</h3>
          <p className={styles.statTitle}>{stat.title}</p>
          <span className={styles.statTrend}>{stat.trend}</span>
        </div>
      </Card>
    </Link>
  );

  const renderModuleCard = (module: any) => {
    if (!hasPermission(module.permission)) {
      return null;
    }

    return (
      <Link to={module.path} key={module.id} className={styles.moduleLink}>
        <Card className={styles.moduleCard}>
          <div className={styles.moduleIcon} style={{ color: module.color }}>
            <module.icon />
          </div>
          <div className={styles.moduleContent}>
            <h3 className={styles.moduleTitle}>{module.title}</h3>
            <p className={styles.moduleDescription}>{module.description}</p>
          </div>
          <div className={styles.moduleArrow}>
            →
          </div>
        </Card>
      </Link>
    );
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <span>Loading admin dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <FiAlertCircle className={styles.errorIcon} />
        <span>{error}</span>
        <Button onClick={loadDashboardData} variant="ghost" size="sm">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.adminDashboard}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1>Admin Dashboard</h1>
          {currentOrganization && (
            <p>
              Managing <strong>{currentOrganization.name}</strong>
              {canManageOrganization() && (
                <span style={{ marginLeft: '0.5rem' }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={openRenameModal}
                    data-testid="admin-rename-organization"
                  >
                    <FiEdit2 />
                    Rename
                  </Button>
                </span>
              )}
            </p>
          )}
          {!currentOrganization && (
            <p>Manage your organization's users, permissions, and security</p>
          )}
        </div>
        <div className={styles.headerActions}>
          <Link to="/settings/profile">
            <Button variant="ghost" size="sm">
              <FiSettings />
              Settings
            </Button>
          </Link>
        </div>
      </div>

      {/* Rename Organization modal (admin/owner only; gated by canManageOrganization). */}
      <Modal
        isOpen={showRenameModal}
        onClose={() => !renaming && setShowRenameModal(false)}
        title="Rename organization"
        size="medium"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            This updates the display name for everyone in this organization.
          </p>
          <Input
            label="Organization name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            disabled={renaming}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRename();
            }}
          />
          {renameError && (
            <p style={{ color: 'var(--color-error)', margin: 0, fontSize: '0.875rem' }}>
              {renameError}
            </p>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <Button variant="secondary" onClick={() => setShowRenameModal(false)} disabled={renaming}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleRename} loading={renaming}>
              Save
            </Button>
          </div>
        </div>
      </Modal>

      {/* Statistics Overview */}
      <div className={styles.statsSection}>
        <h2>Overview</h2>
        <div className={styles.statsGrid}>
          {getStatCards().map(renderStatCard)}
        </div>
      </div>

      {/* Admin Modules */}
      <div className={styles.modulesSection}>
        <h2>Administration</h2>
        <div className={styles.modulesGrid}>
          {ADMIN_MODULES.map(renderModuleCard)}
        </div>
      </div>

    </div>
  );
}